import { ContactAdd } from '#app/routes/mocra+/_form+/components/contacts/add.tsx'
import { ContactCard } from '#app/routes/mocra+/_form+/components/contacts/card.tsx'
import { type ContactDraft } from '#app/routes/mocra+/_form+/components/contacts/types.ts'
import { Section } from '#app/routes/mocra+/_form+/components/section.tsx'
import { type ContactDisplay } from '#app/routes/mocra+/_form+/contacts/reference.ts'
import { type MonitoringOwnerStageKey } from '#app/routes/mocra+/_form+/regulatory-monitoring.tsx'
import { type SectionResources } from '#app/routes/mocra+/_form+/section-resources.ts'

type MonitoringOwnerContactItem = {
	readonly display: ContactDisplay
	readonly errors: readonly string[]
}

type MonitoringOwnerStageView = {
	readonly key: MonitoringOwnerStageKey
	readonly question: string
	readonly description: string
	readonly defaultName: string
	readonly defaultTitle: string
	readonly directorySource: string
	readonly contacts: ReadonlyArray<MonitoringOwnerContactItem>
	readonly existingContacts: ReadonlyArray<ContactDraft>
	readonly listErrors: readonly string[]
}

type Props = {
	readonly resources: SectionResources
	readonly stages: ReadonlyArray<MonitoringOwnerStageView>
	readonly onAddContact: (
		stage: MonitoringOwnerStageKey,
		draft: ContactDraft,
	) => void
	readonly onEditContact: (
		stage: MonitoringOwnerStageKey,
		index: number,
	) => void
	readonly onRemoveContact: (
		stage: MonitoringOwnerStageKey,
		index: number,
	) => void
}

export function MonitoringOwnersSection({
	resources,
	stages,
	onAddContact,
	onEditContact,
	onRemoveContact,
}: Props) {
	return (
		<Section
			title="Monitoring owners"
			description="Assign a point person for each regulatory monitoring focus so updates flow to the right people."
			mocraSections={resources.mocraSections}
			templates={resources.templates}
			childrenClassName="space-y-8"
		>
			{stages.map((stage) => (
				<div key={stage.key} className="space-y-3">
					<div className="space-y-1">
						<p className="text-sm font-medium">{stage.question}</p>
						<p className="text-muted-foreground text-sm">{stage.description}</p>
					</div>
					{stage.listErrors.length ? (
						<ul className="text-destructive list-disc space-y-1 pl-5 text-xs">
							{stage.listErrors.map((error) => (
								<li key={error}>{error}</li>
							))}
						</ul>
					) : null}
					{stage.contacts.length > 0 ? (
						<div className="space-y-2">
							{stage.contacts.map((contact, index) => (
								<div key={`${stage.key}-${index}`} className="space-y-1">
									<ContactCard
										name={contact.display.name}
										title={contact.display.title}
										email={contact.display.email}
										phone={contact.display.phone}
										onClick={() => onEditContact(stage.key, index)}
										onRemove={() => onRemoveContact(stage.key, index)}
										ariaLabel={`${stage.defaultName} contact ${index + 1}`}
										className={
											contact.errors.length > 0
												? 'border-destructive/40 ring-destructive/40 ring-1'
												: undefined
										}
									/>
									{contact.errors.length > 0 ? (
										<p className="text-destructive text-sm">
											{contact.errors[0]}
										</p>
									) : null}
								</div>
							))}
						</div>
					) : null}
					<ContactAdd
						onSubmit={(draft) => onAddContact(stage.key, draft)}
						directorySource={stage.directorySource}
						existingContacts={stage.existingContacts}
					/>
				</div>
			))}
		</Section>
	)
}
